import { FunctionComponent } from 'react';

import { chakra } from '@bonusly/design-system';

import { DefaultShortcode, ShortcodeMap, TypeToUnicodeMap, UnicodeMap } from '../config';
import { ReactComponent as AmazeCoin } from '../images/coin-amaze.svg';
import { ReactComponent as CheerCoin } from '../images/coin-cheer.svg';
import { ReactComponent as HeartCoin } from '../images/coin-heart.svg';
import { ReactComponent as LaughCoin } from '../images/coin-laugh.svg';
import { ReactComponent as ThumbUpCoin } from '../images/coin-thumb-up.svg';
import { ReactComponent as Amaze } from '../images/icon-amaze.svg';
import { ReactComponent as Cheer } from '../images/icon-cheer.svg';
import { ReactComponent as Heart } from '../images/icon-heart.svg';
import { ReactComponent as Laugh } from '../images/icon-laugh.svg';
import { ReactComponent as ThumbUp } from '../images/icon-thumb-up.svg';
import { ReactionType } from '../types';

const AmazeCoinIcon = chakra(AmazeCoin);
const CheerCoinIcon = chakra(CheerCoin);
const HeartCoinIcon = chakra(HeartCoin);
const LaughCoinIcon = chakra(LaughCoin);
const ThumbUpCoinIcon = chakra(ThumbUpCoin);

const AmazeIcon = chakra(Amaze);
const CheerIcon = chakra(Cheer);
const HeartIcon = chakra(Heart);
const LaughIcon = chakra(Laugh);
const ThumbUpIcon = chakra(ThumbUp);

const AllowedShortcodes = Object.keys(ShortcodeMap);

interface ConstructorArgs {
	type?: ReactionType;
	shortcode?: string;
}

const CoinMap: Record<ReactionType, FunctionComponent> = {
	[ReactionType.Like]: ThumbUpCoinIcon,
	[ReactionType.Love]: HeartCoinIcon,
	[ReactionType.Celebrate]: CheerCoinIcon,
	[ReactionType.Amaze]: AmazeCoinIcon,
	[ReactionType.Laugh]: LaughCoinIcon,
};

const IconMap: Record<ReactionType, FunctionComponent> = {
	[ReactionType.Like]: ThumbUpIcon,
	[ReactionType.Love]: HeartIcon,
	[ReactionType.Celebrate]: CheerIcon,
	[ReactionType.Amaze]: AmazeIcon,
	[ReactionType.Laugh]: LaughIcon,
};

export class Reaction {
	public id = '';

	private _type: ReactionType | undefined;
	private _shortcode: string | undefined;
	private _originalShortcode: string | undefined;

	constructor({ type, shortcode }: ConstructorArgs) {
		if (!type && !shortcode) {
			throw new Error('Reaction requires either an `type` or a `shortcode`');
		}

		this._type = type;
		this._shortcode = shortcode;

		if (this._shortcode) {
			const unicodeType = this.getTypeFromUnicode();
			if (unicodeType) {
				this._type = unicodeType;
				this._originalShortcode = this._shortcode;
				this._shortcode = undefined;
			} else {
				const normalized = this.normalizeShortcode() || '';
				if (!AllowedShortcodes.includes(normalized)) {
					this._originalShortcode = this._shortcode;
					this._shortcode = DefaultShortcode;
				}
			}
		}
	}

	public get type() {
		return (this._type || this.getTypeFromShortcode()) as ReactionType;
	}

	public get shortcode() {
		return (this._originalShortcode || this._shortcode || this.getShortcodeFromType()) as string;
	}

	public get unicode() {
		return TypeToUnicodeMap[this.type];
	}

	public get icon() {
		return IconMap[this.type];
	}

	public get coin() {
		return CoinMap[this.type];
	}

	private getTypeFromUnicode(): ReactionType | undefined {
		if (!this._shortcode) return undefined;
		return UnicodeMap[this._shortcode];
	}

	private getTypeFromShortcode(): ReactionType | undefined {
		const shortcode = this.normalizeShortcode();

		if (!shortcode) {
			return undefined;
		}

		return ShortcodeMap[shortcode];
	}

	private getShortcodeFromType() {
		if (!this._type) {
			return undefined;
		}

		const shortcode = Object.entries(ShortcodeMap).find(([, value]) => value === this._type)?.[0];

		return shortcode ? `:${shortcode}:` : undefined;
	}

	private normalizeShortcode() {
		return this._shortcode?.replaceAll(':', '');
	}

	public static fromRecord(id: string, shortcode: string) {
		const instance = new Reaction({ shortcode });

		instance.id = id;

		return instance;
	}

	public static fromShortcode(shortcode: string) {
		return new Reaction({ shortcode });
	}

	public static fromType(type: ReactionType) {
		return new Reaction({ type });
	}
}
